import { motion } from "framer-motion";
import { 
  Activity, Target, Layers, Percent,
  ArrowUpRight, ArrowDownRight, Minus, TrendingUp, FlaskConical
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export default function MetricsPanel({ metrics }) {
  if (!metrics) return null;

  // Helper to interpret enrichment score
  const interpretEnrichment = (e) => {
    if (e > 2) return "Strong";
    if (e > 1.2) return "Moderate";
    if (e >= 0.8) return "Random";
    return "Excluded";
  };

  const metricCards = [
    {
      id: "pearson",
      label: "Pearson r",
      value: metrics.pearson_r,
      format: (v) => v.toFixed(4),
      description: "Correlation coefficient between red and green intensities",
      icon: Activity,
      color: "blue",
      range: [-1, 1],
      interpretation: metrics.pearson_r > 0.5 ? "Strong positive" : 
                      metrics.pearson_r > 0 ? "Weak positive" : 
                      metrics.pearson_r < -0.5 ? "Strong negative" : "Weak negative"
    },
    {
      id: "costes_p",
      label: "Costes' p-value",
      value: metrics.costes_p_value ?? null,
      format: (v) => v === null ? "N/A" : v < 0.001 ? "<0.001" : v.toFixed(4),
      description: "Statistical significance of colocalization (Costes' randomization test). p < 0.05 indicates significant colocalization.",
      icon: FlaskConical,
      color: metrics.costes_significant ? "green" : "yellow",
      interpretation: metrics.costes_p_value === undefined || metrics.costes_p_value === null 
        ? "Not calculated" 
        : metrics.costes_significant 
          ? "Significant (p<0.05)" 
          : "Not significant"
    },
    {
      id: "m1",
      label: "Manders M1",
      value: metrics.manders_m1,
      format: (v) => v.toFixed(4),
      description: "Fraction of red signal colocalizing with green",
      icon: Target,
      color: "red",
      range: [0, 1]
    },
    {
      id: "m2",
      label: "Manders M2",
      value: metrics.manders_m2,
      format: (v) => v.toFixed(4),
      description: "Fraction of green signal colocalizing with red",
      icon: Target,
      color: "green",
      range: [0, 1]
    },
    {
      id: "enrichment_green_at_R",
      label: "Enrich. G at R",
      value: metrics.enrichment_green_at_R || 0,
      format: (v) => v.toFixed(2) + "×",
      description: "Green enrichment in red foreground vs background: mean(G in R+) / mean(G in R-)",
      icon: TrendingUp,
      color: "green",
      interpretation: interpretEnrichment(metrics.enrichment_green_at_R || 0)
    },
    {
      id: "enrichment_red_at_G",
      label: "Enrich. R at G",
      value: metrics.enrichment_red_at_G || 0,
      format: (v) => v.toFixed(2) + "×",
      description: "Red enrichment in green foreground vs background: mean(R in G+) / mean(R in G-)",
      icon: TrendingUp,
      color: "red",
      interpretation: interpretEnrichment(metrics.enrichment_red_at_G || 0)
    },
    {
      id: "coloc",
      label: "Colocalization",
      value: metrics.n_coloc,
      format: (v) => v.toLocaleString(),
      description: "Number of colocalized voxels",
      icon: Layers,
      color: "yellow",
      subtext: `of ${(metrics.n_red_fg + metrics.n_green_fg).toLocaleString()} foreground`
    },
    {
      id: "frac_red",
      label: "Red Colocalized",
      value: metrics.fraction_coloc_red * 100,
      format: (v) => v.toFixed(1) + "%",
      description: "Percentage of red foreground that colocalizes",
      icon: Percent,
      color: "red"
    },
    {
      id: "frac_green",
      label: "Green Colocalized",
      value: metrics.fraction_coloc_green * 100,
      format: (v) => v.toFixed(1) + "%",
      description: "Percentage of green foreground that colocalizes",
      icon: Percent,
      color: "green"
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 0.1 }}
      className="space-y-4"
      data-testid="metrics-panel"
    >
      <div className="flex items-center justify-between">
        <h3 className="font-chivo text-lg font-semibold">Colocalization Metrics</h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground font-mono px-2 py-1 bg-secondary/50 rounded">
            {(metrics.threshold_method || "otsu").toUpperCase()}
          </span>
          {metrics.pearson_mode && (
            <span className="text-xs text-muted-foreground font-mono px-2 py-1 bg-secondary/50 rounded">
              Pearson: {metrics.pearson_mode}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {metricCards.map((card, idx) => (
          <MetricCard key={card.id} {...card} delay={idx * 0.05} />
        ))}
      </div>

      {/* Threshold Info */}
      <div className="mt-4 p-4 rounded-lg bg-secondary/30 space-y-2">
        <p className="text-xs text-muted-foreground uppercase tracking-wide">Thresholds</p>
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span className="font-mono text-sm">{metrics.threshold_red.toFixed(4)}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500" />
            <span className="font-mono text-sm">{metrics.threshold_green.toFixed(4)}</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function MetricCard({ label, value, format, description, icon: Icon, color, subtext, interpretation, delay }) {
  const colorClasses = {
    blue: {
      bg: "bg-blue-500/10",
      text: "text-blue-400",
      glow: "shadow-blue-500/20"
    },
    red: {
      bg: "bg-red-500/10",
      text: "text-red-400",
      glow: "shadow-red-500/20"
    },
    green: {
      bg: "bg-green-500/10",
      text: "text-green-400",
      glow: "shadow-green-500/20"
    },
    yellow: {
      bg: "bg-yellow-500/10",
      text: "text-yellow-400",
      glow: "shadow-yellow-500/20"
    }
  };

  const colors = colorClasses[color] || colorClasses.blue;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay }}
            className={`
              metric-card cursor-help
              hover:shadow-lg transition-shadow duration-300
              ${colors.glow}
            `}
            data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`p-2 rounded-lg ${colors.bg}`}>
                <Icon className={`w-4 h-4 ${colors.text}`} />
              </div>
              {interpretation && (
                <span className="text-xs text-muted-foreground">
                  {interpretation}
                </span>
              )}
            </div>
            
            <p className="text-xs text-muted-foreground mb-1">{label}</p>
            <p className={`font-mono text-2xl font-bold ${colors.text}`}>
              {value === null || value === undefined ? "N/A" : format(value)}
            </p>
            
            {subtext && (
              <p className="text-xs text-muted-foreground mt-1">{subtext}</p>
            )}
          </motion.div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[200px]">
          <p className="text-sm">{description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
